/**
 * useAvatarRAG Hook
 *
 * React hook that wraps AvatarRAGEngine for Expert RAG mode in avatar sessions.
 * Manages the STT → RAG → Echo pipeline lifecycle and exposes state + sources
 * for the avatar conversation view.
 */

import { useCallback, useRef, useState } from 'react'
import {
  AvatarRAGEngine,
  type AvatarRAGCallbacks,
  type AvatarRAGState,
} from '@/services/avatar/AvatarRAGEngine'
import type { AvatarSession } from '@/connectors/avatar'
import type { RAGSource } from '@/types/source'
import type { RagConfig } from '@/stores/personaStore'
import type { ChatMessage } from '@/services/ragConnector'
import type { SpeechMode } from '@/utils/voiceDelivery'

export interface AvatarRAGMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: RAGSource[]
  isStreaming?: boolean
}

interface UseAvatarRAGOptions {
  onError?: (error: Error) => void
}

export function useAvatarRAG(options: UseAvatarRAGOptions = {}) {
  const engineRef = useRef<AvatarRAGEngine | null>(null)

  const [ragState, setRagState] = useState<AvatarRAGState>('idle')
  const [messages, setMessages] = useState<AvatarRAGMessage[]>([])
  const [currentSources, setCurrentSources] = useState<RAGSource[]>([])
  const [audioLevel, setAudioLevel] = useState(0)

  // Ref for accumulating streaming assistant content
  const streamingContentRef = useRef('')
  const messageIdCounter = useRef(0)

  const nextId = () => {
    messageIdCounter.current += 1
    return `avatar-rag-${messageIdCounter.current}`
  }

  /**
   * Start the RAG engine for an active avatar session.
   */
  const startRAG = useCallback(
    async (
      session: AvatarSession,
      ragConfig: RagConfig,
      ragServerUrl: string,
      ragApiKey: string,
      existingHistory?: ChatMessage[],
      speechMode: SpeechMode = 'verbatim'
    ) => {
      // Clean up any previous engine
      if (engineRef.current) {
        await engineRef.current.stop()
      }

      // Reset UI state
      setMessages([])
      setCurrentSources([])
      streamingContentRef.current = ''

      const callbacks: AvatarRAGCallbacks = {
        onUserMessage: (text) => {
          setMessages((prev) => [
            ...prev,
            { id: nextId(), role: 'user', content: text },
          ])
          // Clear sources from previous turn
          setCurrentSources([])
        },
        onAssistantChunk: (chunk) => {
          streamingContentRef.current += chunk

          setMessages((prev) => {
            const last = prev[prev.length - 1]
            if (last?.role === 'assistant' && last.isStreaming) {
              // Update the streaming message
              return [
                ...prev.slice(0, -1),
                { ...last, content: streamingContentRef.current },
              ]
            }
            // Start a new streaming message
            streamingContentRef.current = chunk
            return [
              ...prev,
              {
                id: nextId(),
                role: 'assistant' as const,
                content: chunk,
                isStreaming: true,
              },
            ]
          })
        },
        onAssistantComplete: (fullText) => {
          streamingContentRef.current = ''

          setMessages((prev) => {
            const last = prev[prev.length - 1]
            if (last?.role === 'assistant' && last.isStreaming) {
              return [
                ...prev.slice(0, -1),
                { ...last, content: fullText, isStreaming: false },
              ]
            }
            return prev
          })
        },
        onSources: (sources) => {
          setCurrentSources(sources)
          // Attach sources to the current assistant message when it completes
          setMessages((prev) => {
            const last = prev[prev.length - 1]
            if (last?.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                { ...last, sources },
              ]
            }
            return prev
          })
        },
        onStateChange: (state) => {
          setRagState(state)
        },
        onError: (error) => {
          console.error('[useAvatarRAG] Error:', error)
          options.onError?.(error)
        },
        onAudioLevel: (level) => {
          setAudioLevel(level)
        },
      }

      const engine = new AvatarRAGEngine(
        session,
        callbacks,
        ragConfig,
        ragServerUrl,
        ragApiKey,
        speechMode,
      )

      if (existingHistory) {
        engine.setConversationHistory(existingHistory)
      }

      engineRef.current = engine
      await engine.start()
    },
    [options]
  )

  /**
   * Stop the RAG engine and clean up.
   */
  const stopRAG = useCallback(async () => {
    if (engineRef.current) {
      await engineRef.current.stop()
      engineRef.current = null
    }
    setRagState('idle')
  }, [])

  /**
   * Interrupt current processing.
   */
  const interruptRAG = useCallback(() => {
    engineRef.current?.interrupt()
  }, [])

  return {
    // State
    ragState,
    messages,
    currentSources,
    audioLevel,
    isListening: ragState === 'listening',
    isProcessing: ragState === 'processing',

    // Actions
    startRAG,
    stopRAG,
    interruptRAG,
  }
}
